import {html} from 'lit';
import InputText from '../input/input-text.js';
import '../grid/grid-col.js';
import '../grid/grid-row.js';
import '../icon/icon.js';
import '../form/label/label.js';
import './dropdown-item.js';

import styles from './dropdown.css' assert {type: 'css'};

/**
 * @element inv-input-select
 *
 * @class Dropdown
 * @classdesc This is a web component that creates a dropdown.
 *
 * property {Array} options - The options of the dropdown.
 * property {boolean} search - Whether the dropdown should be searchable.
 * property {string} value - The value of the dropdown.
 * property {string} label - The label of the dropdown.
 *
 * @cssproperty --inv-dropdown-z-index
 * @description The z-index value for the dropdown menu.
 * @type {number}
 * @default 1000
 *
 * @cssproperty --inv-dropdown-min-width
 * @description The minimum width of the dropdown menu.
 * @type {string}
 * @default 10em
 *
 * @cssproperty --inv-dropdown-max-width
 * @description The maximum height of the dropdown menu.
 * @type {string}
 * @default 10em
 *
 * @cssproperty --inv-dropdown-padding-y
 * @description The vertical padding of the dropdown menu.
 * @type {string}
 * @default 0.5em
 *
 * @cssproperty --inv-dropdown-padding-x
 * @description The horizontal padding of the dropdown menu.
 * @type {string}
 * @default 0
 *
 * @cssproperty --inv-dropdown-font-size
 * @description The font size of the dropdown menu.
 * @type {string}
 * @default 1em
 *
 * @cssproperty --inv-dropdown-color
 * @description The font color of the dropdown menu.
 * @type {string}
 * @default #212529
 *
 * @cssproperty --inv-dropdown-background
 * @description The background color of the dropdown menu.
 * @type {string}
 * @default #fff
 *
 * @cssproperty --inv-dropdown-border-width
 * @description The border width of the dropdown menu.
 * @type {string}
 * @default 0.05em
 *
 * @cssproperty --inv-dropdown-border-color
 * @description The border color of the dropdown menu.
 * @type {string}
 * @default rgba(0, 0, 0, 0.175)
 *
 * @cssproperty --inv-dropdown-border-radius
 * @description The border radius of the dropdown menu.
 * @type {string}
 * @default 0.375em
 *
 * @cssproperty --inv-font-family
 * @description The font family for the badge.
 * @type {string}
 * @default "Inter"
 */
export default class Dropdown extends InputText {
    static get properties() {
        return {
            ...InputText.properties,
            options: {
                type: Array,
            },
            search: {
                type: Boolean,
            },
            value: {
                type: String,
                reflect: true,
            },
        };
    }

    constructor() {
        super();
        this.options = [{value: 'None', label: 'None'}];
        this.selectedValue = null;
        this.selectedLabel = null;
        this.search = false;
        this.icon = 'arrow-down';
    }

    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    get #dropdownMenuId() {
        return 'dropdown-menu-' + this.getId();
    }

    get #dropdownMenu() {
        return this._getElementById(this.#dropdownMenuId);
    }

    get #input() {
        return this.renderRoot?.querySelector(`input[name=${this.name}]`);
    }

    setReadOnly() {
        const input = this.#input;
        if (!this.search) {
            input.setAttribute('readonly', true);
        }
    }

    update(changedProperties) {
        super.update(changedProperties);
        this.setReadOnly();
        this.onLoadDropdownItems();
    }

    onLoadDropdownItems() {
        const dropdownMenu = this.#dropdownMenu;
        if (dropdownMenu) {
            // if search is true just show the related items if it isnt true
            // it shows all the item list
            const value = this.search === true ? this.value ?? '' : '';
            dropdownMenu.replaceChildren();
            if ((value || value === '') && this.options) {
                const valueInUpperCase = value.toUpperCase();
                this.appendChildren(
                    this.options
                        .map((option) => {
                            let item = null;
                            if (option.label.toUpperCase().includes(valueInUpperCase) || value === '') {
                                item = document.createElement('inv-dropdown-item');
                                item.value = option.value;
                                item.label = option.label;
                                item.onclick = () => {
                                    this.value = option.label;
                                    this.selectedValue = option.value;
                                    this.selectedLabel = option.label;
                                    this.emit(`change`, {value: option.value});
                                    this.removeClasses(this.#dropdownMenuId, 'show');
                                };
                            }
                            return item;
                        })
                        .filter((item) => item),
                    dropdownMenu
                );
            }
        }
    }

    onInput = (e) => {
        this.value = e.target.value;
        this.onLoadDropdownItems();
    };
    onFocusIn = () => {
        this.emit('focus-in', {value: this.selectedValue});
        this.addClasses(this.#dropdownMenuId, 'show');
    };

    onFocusOut = (e) => {
        if (e.relatedTarget?.id !== this.#dropdownMenuId && e.relatedTarget?.id !== this.getId()) {
            this.removeClasses(this.#dropdownMenuId, 'show');
            this.value = this.selectedLabel ?? this.value;
            this.emit('focus-out', {value: this.selectedValue});
        }
    };

    /**
     * Manejador heredado de InputText que se encarga de hacer focus en el input
     * si se hace click en el icono
     */
    onIconClick = () => {
        const input = this.#input;
        if (input) {
            input.focus();
            this.onFocusIn();
        }
    };

    renderInput(childNodes) {
        return html`
            <div
                class="input-container ${this.getCss(['error', 'disabled'])}"
                style="position: relative"
                @focusout="${this.buildHandler('focus-out')}"
            >
                <input
                    @input="${this.buildHandler('input')}"
                    @focusin="${this.buildHandler('focus-in')}"
                    .value="${this.value}"
                    class="${this.getCss('error')}"
                    id="${this.getId()}"
                    name="${this.name}"
                    placeholder="${this.placeholder}"
                    ?disabled="${this.disabled}"
                />
                <ul tabindex="-1" id="${this.#dropdownMenuId}" class="dropdown-menu"></ul>
                ${childNodes}
            </div>
        `;
    }
}

Dropdown.register();
